import _ from 'lodash';
import * as constants from '../Actions/types';
import initialState from './initial-state';

// A coding the user has started but not sent yet only exists in the browser: the
// server copy of that transaction is still uncoded. The background refreshes below
// merge the server copy straight over the top, which wiped the staged coding and
// dropped Coding to zero entries. That disables the contact, tax code and
// classification cells (disabledCellChecker in TransactionDataTable), so tabbing off
// the contact skipped classification -- the last tab stop in the row -- wrapped to
// the next row and made leaveRow post the half finished coding, with a contact but
// no class. For RAH the queue is polled every five seconds while transactions are
// syncing, so it landed mid edit often enough to look random (RWR-19694).
// Rows staged by updateTransactionsList are held back until the coding has been sent
// (SET_CODED clears the flag), and are kept by reference so the row does not
// re-render for a change it is ignoring.
// `staged` on an UPDATE_TRANSACTIONS is three-valued: true when the action is staging
// a coding edit, false when it is discarding one, and absent for the updates that do
// neither -- a comment, a transaction lock, an unmatch. Those must leave an already
// staged row staged: they do not send the coding, so clearing the flag would hand the
// row straight back to the next background refresh (RWR-19694).
const stagedFlag = (transactionsList, action) => {
  if (action.staged === true) return true;
  if (action.staged === false) return undefined;
  const current = transactionsList[action.payload.BankDataGUID];
  return (current && current.stagedCoding) || undefined;
};

const mergeServerTransactions = (transactionsList, payload) => {
  const serverRows = _.mapKeys(payload, 'BankDataGUID');
  Object.keys(serverRows).forEach((BankDataGUID) => {
    const stagedRow = transactionsList[BankDataGUID];
    if (stagedRow && stagedRow.stagedCoding) serverRows[BankDataGUID] = stagedRow;
  });
  return { ...transactionsList, ...serverRows };
};

export default function(state = initialState, action) {
  switch (action.type) {
    case constants.IS_SAVING:
      return { ...state, transactionsList: { ...state.transactionsList, [action.payload]: { ...state.transactionsList[action.payload], isSaving: true } } };

    case constants.IS_SAVED:
      return { ...state, transactionsList: { ...state.transactionsList, [action.payload]: { ...state.transactionsList[action.payload], isSaving: false } } };

    case constants.IS_LOADING_TRANSACTIONS:
      return { ...state, isLoadingTransactions: action.payload };

    case constants.GET_TRANSACTIONS:
      return { ...state, filename: action.filename, transactionsList: _.mapKeys(action.payload, 'BankDataGUID'), matches: action.matches ? action.matches : [...state.matches], timeStamp: (action.timeStamp) ? action.timeStamp : state.timeStamp, isLoadingTransactions: false };

    case constants.NEW_TRANSACTIONS:
      return { ...state, transactionsList: mergeServerTransactions(state.transactionsList, action.payload), timeStamp: (action.timeStamp) ? action.timeStamp : state.timeStamp };

    case constants.UPDATE_TRANSACTIONS:
      return { ...state, transactionsList: { ...state.transactionsList, [action.payload.BankDataGUID]: {...action.payload, notCoded: true, stagedCoding: stagedFlag(state.transactionsList, action) } } };

    case constants.QUEUE_TRANSACTION_UPDATE:
      return { ...state, transactionsList: mergeServerTransactions(state.transactionsList, action.payload), matches: action.matches ? action.matches : [...state.matches], timeStamp: action.timeStamp };
    
    case constants.SELECT_TRANSACTION:
      return { ...state, selectedTransactions: [ ...state.selectedTransactions, action.payload ] }

    case constants.DESELECT_TRANSACTION:
        return { ...state, selectedTransactions:  state.selectedTransactions.filter(transaction => transaction.BankDataGUID !== action.payload.BankDataGUID) };
    
    case constants.SELECT_TRANSACTIONS:
        return { ...state, selectedTransactions: action.payload }

    case constants.UPDATE_FILTERBY:
        return { ...state, filterBy: action.payload }

    case constants.SET_CODED:
      return { ...state,
        transactionsList: { ...state.transactionsList,
          [action.payload.BankDataGuid]: { ...state.transactionsList[action.payload.BankDataGuid], ...action.payload, lockedDetails: action.coded } } };

    // Dispatched once the coding POST has settled, whichever way it went. On success
    // the row is on the server; on failure the coding never got there and the row has
    // to go back to taking server updates so a later refresh can correct it. Either
    // way it no longer needs holding back.
    case constants.CODING_SETTLED:
      return { ...state,
        transactionsList: { ...state.transactionsList,
          [action.payload]: { ...state.transactionsList[action.payload], stagedCoding: undefined } } };
    
    case constants.CODED_BY_R1_RULE:
      return { ...state, 
        transactionsList: { ...state.transactionsList, 
          [action.payload.BankDataGUID]: { ...state.transactionsList[action.payload.BankDataGUID], ...action.payload } } };  

    case constants.GET_BANK_ACCOUNTS:
      return { ...state, bankAccounts: action.payload };

    case constants.GET_CHART:
      return { ...state, transactionCodes: action.payload };

    case constants.GET_CONTACTS:
      return { ...state, contacts: action.payload };

    case constants.GET_PROJECTS:
      return { ...state, projects: action.payload };

    case constants.GET_CLASSIFICATION:
      return { ...state, classification: action.payload };

    case constants.GET_TAX_PREFERENCE:
      return { ...state, taxPreference: action.payload };

    case constants.SET_API_ERROR:
      return { ...state, apiError: action.payload };

    case constants.LOAD_COMPLETED:
      return { ...state, loadCompleted: action.payload };

    case constants.UPDATE_MATCHES:
      return { ...state, matches: action.payload };

    case constants.REMOVE_MATCH_BUTTON:
      return { ...state, removeMatchList:  [ ...state.removeMatchList, action.payload ] }

    // Puts the Match button back after a match the API refused, so the transaction can
    // be matched against something else without reloading the page (RWR-20040).
    case constants.RESTORE_MATCH_BUTTON:
      return { ...state, removeMatchList: state.removeMatchList.filter(BankDataGuid => BankDataGuid !== action.payload) }

    case constants.START_MATCH_LOADING:
        return { ...state, matchLoadingList:  [ ...state.matchLoadingList, action.payload ] }
    
    case constants.STOP_MATCH_LOADING:
        return { ...state, matchLoadingList:  [ ...state.matchLoadingList.filter(BankDataGuid => BankDataGuid !== action.payload) ] }
    
    case constants.SORT_ROWS:
      return { ...state, transactionsList: _.mapKeys(action.payload, 'BankDataGUID') };

    case constants.TRANSACTION_CODING_ERROR:
        return { ...state, codingErrors: [ ...state.codingErrors, action.payload ] }

    default:
      return state;
  }
}


// WEBPACK FOOTER //
// ./src/reducers/transactionsReducer.js